import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Avatar, Box, Grid, TextField, InputAdornment, MenuItem } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AutocompleteSearchUser from 'commons/AutocompleteSearchUser';
import DialogCustom from 'commons/DialogCustom';
import SelectCustom from 'commons/SelectCustom';
import Text from 'commons/Text';
import API from 'configs/API';
import { rolesExceptArtist } from 'configs/Constant';
import useDebounce from 'hooks/useDebounce';
import { axiosPost } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import useStyles from './styles';

const DialogTransferOwnerArtwork = ({ open, onClose, artwork }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.account.user);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEdition, setSelectedEdition] = useState('');
  const [inputValue, setInputValue] = useState('');
  const debounceInputValue = useDebounce(inputValue);
  const classes = useStyles();
  const queryClient = useQueryClient();

  const editions = Array.isArray(artwork?.editions) ? artwork.editions : [];
  const isMultipleEditions = artwork?.total_edition > 1;
  const disabled = !selectedUser?.id || (isMultipleEditions && !selectedEdition);

  const transferOwnerMutation = useMutation({
    mutationFn: ({ code, recipient_user_id }) => {
      const urlFn = API.ARTWORK.CERTIFICATE_TRANSFER_OWNER_ROLE_ARTIST;
      return axiosPost(urlFn(code), { recipient_user_id });
    },
  });

  const handleSubmit = async () => {
    if (!selectedUser?.id) return;

    console.log('selectedEdition', selectedEdition);
    const codeCertificate = selectedEdition;

    if (!codeCertificate) {
      ToastTopHelper.error(t('update_failed'));
      return;
    }

    const { success } = await transferOwnerMutation.mutateAsync({
      code: codeCertificate,
      recipient_user_id: selectedUser.id,
    });
    if (success) {
      ToastTopHelper.success(t('transfer_success'));
      queryClient.invalidateQueries({
        queryKey: [API.ARTWORK.LIST_ARTWORK_EXCLUDING_SOLD_DONATED_GIFTED],
      });
      queryClient.invalidateQueries({
        queryKey: [API.ARTWORK.ARTWORK],
      });
      onClose?.();
    }
  };

  const { data: userFilter = [] } = useQuery({
    queryKey: [
      API.AUTH.SEARCH_USER,
      {
        search: debounceInputValue || 'l',
        role__in: rolesExceptArtist,
        exclude__in: user?.id,
      },
    ],
  });

  return (
    <DialogCustom
      isShowDialog={open}
      onCancel={onClose}
      onSubmit={handleSubmit}
      dialogTitle={t('tranfer_owner')}
      textSubmit={t('confirm')}
      disabledButton={disabled || transferOwnerMutation.isLoading}
      maxWidth="sm"
    >
      <Box className={classes.contentRoot}>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <div className={classes.labelRow}>
              <Text
                className={classes.label}
                type="sm"
              >
                {t('transfer_to')}
              </Text>
              <span className={classes.require}>*</span>
            </div>
            <AutocompleteSearchUser
              value={selectedUser}
              onChange={(e, val) => setSelectedUser(val)}
              userFilter={userFilter}
              inputValue={inputValue}
              onInputChange={(e, val) => setInputValue(val || '')}
              titleList={t('search_owner')}
              selectUser={selectedUser}
              autocompleteStyle={classes.autoSize}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('search_owner_name')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <SearchIcon className={classes.searchIcon} />
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    startAdornment: selectedUser ? (
                      <InputAdornment
                        position="start"
                        className={classes.adornmentStart}
                      >
                        <Avatar
                          src={selectedUser?.avatar}
                          alt={selectedUser?.name}
                          className={classes.avatar}
                        />
                      </InputAdornment>
                    ) : (
                      params.InputProps.startAdornment
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <div className={classes.labelRow}>
              <Text
                className={classes.label}
                type="sm"
              >
                {t('select_edition')}
              </Text>
              <span className={classes.require}>*</span>
            </div>
            <SelectCustom
              value={selectedEdition}
              onChange={(e) => setSelectedEdition(e.target.value)}
              displayEmpty
              className={classes.selectEdition}
            >
              {editions.map((edition) => (
                <MenuItem
                  key={edition.id}
                  value={edition?.id_certificate}
                >
                  {t('edition')} {edition.edition_number}
                  {edition.id_certificate && ` ( ${t('has_certificate')} )`}
                </MenuItem>
              ))}
            </SelectCustom>
          </Grid>

          <Grid
            item
            xs={12}
          >
            <div className={classes.infoBlock}>
              <div className={classes.noteHeader}>
                <InfoOutlinedIcon className={classes.noteIcon} />
                <Text
                  className={classes.noteTitle}
                  fontWeightBold
                  type="sm"
                >
                  {`${t('note')}:`}
                </Text>
              </div>
              <Text
                className={classes.infoIntro}
                type="sm"
              >
                {t('transfer_owner_info_intro')}
              </Text>
              <ul className={classes.infoList}>
                <li>
                  <Text
                    className={classes.infoItem}
                    type="sm"
                  >
                    {t('transfer_owner_info_point1')}
                  </Text>
                </li>
                <li>
                  <Text
                    className={classes.infoItem}
                    type="sm"
                  >
                    {t('transfer_owner_info_point2')}
                  </Text>
                </li>
              </ul>
            </div>
          </Grid>
        </Grid>
      </Box>
    </DialogCustom>
  );
};

export default DialogTransferOwnerArtwork;
